import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SparkMD5 from 'spark-md5';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import UserAccount from '../../services/UserAccount';
import Company from '../../services/Company';
import AssetApi from '../../services/server/AssetApi';
import MediaUploadTile from '../MediaUploadTile/index';
import ConfirmDialog from '../ConfirmDialog/index';
import Popup from '../Popup';
import ProgressBar from '../ProgressBar';
import { validateContentSourceUrl } from '../../utils/checkContentType';
import Constants from '../../submodules/logictry_config/constants';
import AppConstants from '../../submodules/logictry_config/s3_config.json';
import {
  InputBlock,
  Label,
  Input,
} from '../../styledhtml/Tailwind';

/** Less vertical gap than default InputBlock (20px) between stacked rows. */
const SourceFieldBlock = styled(InputBlock)`
  margin-bottom: 0.5rem;
`;

const InputGrow = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

/** Row: URL + upload tile; vertical centering keeps the tile aligned with the text field. */
const SourceFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  > :last-child {
    flex-shrink: 0;
  }
`;

const FolderHost = styled.div`
  position: fixed;
  width: 0;
  height: 0;
  overflow: hidden;
  left: -100vw;
  top: 0;
`;

const PopupContent = styled.div`
  padding: 2rem;
`;

/** Bounds upload tile; width/height align with merged preview styles. */
const UploadSlot = styled.div`
  position: relative;
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
`;

/** Content Source row: URL input + dashed upload (camera / file / optional folder for dev). */
export default class ContentSourceField extends LogictryComponent {
  static propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
    validateUrlOnBlur: PropTypes.bool,
    inputStyle: PropTypes.object,
    s3Path: PropTypes.string.isRequired,
    signingUrl: PropTypes.string.isRequired,
    onUploadFinish: PropTypes.func,
    hideUploadText: PropTypes.bool,
    allowFolderUpload: PropTypes.bool,
    onFolderUploadComplete: PropTypes.func,
    uploadChoiceMenu: PropTypes.bool,
    onlyAllowImage: PropTypes.bool,
    /**
     * `menu`: full “Add media” sheet (camera / file / etc.).
     * `imageFile`: image/* file picker only (no sheet).
     * `file`: any file picker only (no sheet).
     * `folder`: directory upload only (dev, non‑iPhone); requires `onFolderUploadComplete`.
     * When omitted, uses `uploadChoiceMenu` + `onlyAllowImage` as before.
     */
    uploadPickerMode: PropTypes.oneOf(['menu', 'imageFile', 'file', 'folder']),
    queueUpload: PropTypes.bool,
    onUploadQueued: PropTypes.func,
    inputAriaLabel: PropTypes.string,
    /** Optional; when omitted the upload tile uses `value` so previews match the URL field (settings, templates). */
    uploadBackgroundImage: PropTypes.string,
    /** Override empty-tile color; default matches answer/settings surfaces. */
    uploadSlotBackgroundColor: PropTypes.string,
    uploadAllowDeleting: PropTypes.bool,
    uploadOnDelete: PropTypes.func,
    /** Merged onto default upload tile style (defaults: 16:9 thumbs; square when `uploadPickerMode` is `file`). */
    uploadPreviewStyle: PropTypes.object,
    /** Passed through to `MediaUploadTile` for signed URL requests (e.g. team). */
    additionalQueryParams: PropTypes.object,
    /** When true (default), in-app camera recordings open a review screen before upload. */
    reviewRecordedVideo: PropTypes.bool,
    /** Disable URL input + hide upload tile (e.g. livestream-managed content URL). */
    disabled: PropTypes.bool,
    /** Optional hint under the field when disabled. */
    disabledHint: PropTypes.string,
  }

  state = {
    urlBlurError: null,
    folderUploadTotal: 0,
    folderUploadComplete: 0,
    folderError: '',
  }

  folderInputEl = null

  getFolderSigningApiPath = () => {
    if (Company.company && Company.company._id) {
      return AppConstants.S3.signingUrls.companyfiles;
    }
    return AppConstants.S3.signingUrls.userfiles;
  }

  getFolderUploadPathPrefix = () => {
    if (Company.company && Company.company._id) {
      return `${Company.company._id}/${UserAccount.account._id}/`;
    }
    return `${UserAccount.account._id}/`;
  }

  openFolderPicker = () => {
    if (this.folderInputEl) this.folderInputEl.click();
  }

  handleFolderInputChange = async (e) => {
    if (!e.target || !e.target.files || !e.target.files.length) {
      return this.setState({ folderError: 'You must have at least one file' });
    }
    const path = this.getFolderUploadPathPrefix();
    const signingUrl = this.getFolderSigningApiPath();
    const fileArray = Array.from(e.target.files);
    let totalSize = 0;
    let minDirectorySize = Infinity;
    fileArray.forEach((file) => {
      totalSize += file.size;
      minDirectorySize = Math.min(minDirectorySize, file.webkitRelativePath.split('/').length);
    });

    if (totalSize > 20000000) return this.setState({ folderError: 'There is a 20 MB limit' });

    const indexFound = fileArray.find(({ webkitRelativePath }) => (
      webkitRelativePath.split('/').slice(minDirectorySize - 1).join('/') === 'index.html'
    ));
    if (!indexFound) return this.setState({ folderError: 'You must have one index.html file to upload' });

    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    const computeFileHash = (i) => {
      const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
      const file = fileArray[i];
      const chunkSize = 2097152;
      const chunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;
      fileReader.onload = (ev) => {
        spark.append(ev.target.result);
        currentChunk++;
        if (currentChunk < chunks) {
          loadNext();
        } else {
          const next = i + 1;
          if (fileArray[next]) computeFileHash(next);
          else this.uploadFolderFiles(path, fileArray, signingUrl, minDirectorySize, spark.end());
        }
      };
      fileReader.onerror = () => {
        this.setState({
          folderError: 'Upload Failed',
        });
      };
      function loadNext() {
        const start = currentChunk * chunkSize;
        const end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
      }
      loadNext();
    };
    computeFileHash(0);
    if (e.target) e.target.value = '';
  }

  uploadFolderFiles = async (path, fileArray, signingApiPath, minDirectorySize, hash) => {
    const results = await AssetApi.getFolderSignedUrls(signingApiPath, {
      files: fileArray.map(({ webkitRelativePath, type }) => ({
        path: `${path}${hash}/`,
        objectName: webkitRelativePath.split('/').slice(minDirectorySize - 1).join('/'),
        contentType: type,
      })),
    });
    if (!results || results.error) return this.setState({ folderError: 'Unable to upload at this time' });

    this.setState({ folderError: '', folderUploadTotal: results.length, folderUploadComplete: 0 });
    results.forEach(({ file, sign }) => {
      const request = new XMLHttpRequest();
      request.onreadystatechange = (evt) => {
        if (evt.target.readyState !== 4) return;
        this.setState((prev) => {
          const newComplete = prev.folderUploadComplete + 1;
          const isDone = prev.folderUploadTotal === newComplete;
          if (isDone) {
            const indexFileFound = results.find(({ file: resultFile }) => resultFile.objectName === 'index.html');
            const baseUrl = indexFileFound.sign.signedUrl.split('?')[0];
            queueMicrotask(() => {
              if (this.props.onChange) this.props.onChange(baseUrl);
              if (this.props.onFolderUploadComplete) this.props.onFolderUploadComplete(baseUrl);
            });
          }
          return { folderUploadComplete: newComplete };
        });
      };
      const matchedFile = fileArray.find(
        ({ webkitRelativePath }) => webkitRelativePath.split('/').slice(minDirectorySize - 1).join('/') === file.objectName,
      );
      if (!matchedFile) return;
      request.open('PUT', sign.signedUrl);
      request.setRequestHeader('Content-Disposition', 'inline');
      request.setRequestHeader('Content-Type', matchedFile.type);
      request.send(matchedFile);
    });
  }

  handleUrlBlur = () => {
    if (!this.props.validateUrlOnBlur) return;
    const trimmed = (this.props.value || '').trim();
    if (!trimmed) return;
    const r = validateContentSourceUrl(trimmed, { emptyOk: true });
    if (!r.ok) this.setState({ urlBlurError: r.error });
  };

  clearUrlBlurError = () => this.setState({ urlBlurError: null })

  clearFolderError = () => this.setState({ folderError: '' })

  render() {
    const { isDevAccount } = UserAccount;
    const {
      label,
      placeholder = 'https://...',
      value = '',
      maxLength = 1000,
      onChange,
      validateUrlOnBlur,
      inputStyle,
      s3Path,
      signingUrl,
      onUploadFinish,
      hideUploadText = true,
      allowFolderUpload = false,
      onFolderUploadComplete,
      uploadChoiceMenu = true,
      onlyAllowImage = false,
      uploadPickerMode,
      queueUpload,
      onUploadQueued,
      inputAriaLabel,
      uploadBackgroundImage,
      uploadSlotBackgroundColor,
      uploadAllowDeleting,
      uploadOnDelete,
      uploadPreviewStyle,
      additionalQueryParams,
      reviewRecordedVideo = true,
      disabled = false,
      disabledHint = '',
    } = this.props;

    let effectiveUploadChoiceMenu = uploadChoiceMenu;
    let effectiveOnlyAllowImage = onlyAllowImage;
    if (uploadPickerMode === 'menu') {
      effectiveUploadChoiceMenu = true;
    } else if (uploadPickerMode === 'imageFile') {
      effectiveUploadChoiceMenu = false;
      effectiveOnlyAllowImage = true;
    } else if (uploadPickerMode === 'file' || uploadPickerMode === 'folder') {
      effectiveUploadChoiceMenu = false;
      effectiveOnlyAllowImage = false;
    }

    const canUseFolderUpload = !!(
      isDevAccount
      && onFolderUploadComplete
      && !Constants.isIphone
    );
    const showFolderChoice = !!(
      effectiveUploadChoiceMenu
      && allowFolderUpload
      && canUseFolderUpload
    );
    const folderOnlyTile = uploadPickerMode === 'folder' && canUseFolderUpload;

    const uploadProps = {};
    if (showFolderChoice || folderOnlyTile) uploadProps.onRequestFolderUpload = this.openFolderPicker;

    const tilePreviewUrl =
      typeof uploadBackgroundImage === 'string'
        ? uploadBackgroundImage
        : (value || '');
    const slotBg = uploadSlotBackgroundColor ?? Constants.MediumBackground;

    const defaultUploadPreviewSize =
      uploadPickerMode === 'file'
        ? { width: 45, height: 45 }
        : { width: 80, height: 45 };

    const previewMerged = {
      ...defaultUploadPreviewSize,
      backgroundSize: 'cover',
      borderRadius: 8,
      border: '1px dashed',
      ...(uploadPreviewStyle || {}),
    };

    const uploadSlotLayout = {
      width: previewMerged.width,
      height: previewMerged.height,
      borderRadius: previewMerged.borderRadius,
    };

    const needsFolderHostInput = !disabled && (showFolderChoice || folderOnlyTile);
    const showUploadTile = !disabled && !(uploadPickerMode === 'folder' && !canUseFolderUpload);
    const { folderUploadTotal, folderUploadComplete, folderError } = this.state;
    const folderProgress = folderUploadTotal ? (folderUploadComplete / folderUploadTotal) * 100 : 0;

    return (
      <>
        <SourceFieldBlock>
          {label && <Label>{label}</Label>}
          <SourceFieldRow style={inputStyle}>
            <InputGrow>
              <Input
                type="text"
                aria-label={inputAriaLabel}
                placeholder={placeholder}
                maxLength={maxLength}
                value={value || ''}
                disabled={disabled}
                readOnly={disabled}
                onChange={(e) => {
                  if (disabled) return;
                  const v = e.target.value;
                  if (validateUrlOnBlur && this.state.urlBlurError) this.clearUrlBlurError();
                  if (onChange) onChange(v);
                }}
                onBlur={disabled ? undefined : this.handleUrlBlur}
              />
            </InputGrow>
            {showUploadTile && (
              <UploadSlot style={uploadSlotLayout}>
                <MediaUploadTile
                  s3Path={s3Path}
                  signingUrl={signingUrl}
                  additionalQueryParams={additionalQueryParams}
                  backgroundImage={tilePreviewUrl}
                  backgroundColor={slotBg}
                  hideUploadText={hideUploadText}
                  uploadChoiceMenu={effectiveUploadChoiceMenu}
                  onlyAllowImage={effectiveOnlyAllowImage}
                  folderPickOnly={folderOnlyTile}
                  queueUpload={queueUpload}
                  onUploadQueued={onUploadQueued}
                  onUploadFinish={onUploadFinish}
                  allowDeleting={!!uploadAllowDeleting}
                  onDelete={uploadOnDelete}
                  reviewRecordedVideo={reviewRecordedVideo}
                  {...uploadProps}
                  style={previewMerged}
                />
              </UploadSlot>
            )}
          </SourceFieldRow>
          {disabled && disabledHint ? (
            <div style={{
              marginTop: '0.35rem',
              fontSize: Constants.SemiSmallFontSize,
              color: Constants.MediumTextGray,
              lineHeight: 1.35,
            }}
            >
              {disabledHint}
            </div>
          ) : null}
        </SourceFieldBlock>

        {needsFolderHostInput && (
          <FolderHost aria-hidden="true">
            <input
              ref={(el) => { this.folderInputEl = el; }}
              type="file"
              webkitdirectory="true"
              multiple
              onChange={this.handleFolderInputChange}
            />
          </FolderHost>
        )}

        {folderUploadTotal > 0 && folderUploadTotal !== folderUploadComplete && (
          <Popup>
            <PopupContent>
              <h1>Upload Progress</h1>
              <ProgressBar progress={folderProgress} primaryColor={Constants.PrimaryColor} />
            </PopupContent>
          </Popup>
        )}

        <ConfirmDialog
          open={!!this.state.urlBlurError}
          text={this.state.urlBlurError || ''}
          description=""
          onOk={this.clearUrlBlurError}
        />
        {!!folderError && (
          <ConfirmDialog
            open
            text="Upload Failed"
            description={folderError}
            onOk={this.clearFolderError}
          />
        )}
      </>
    );
  }
}
